import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import API from "../utils/API";
import { useAuth } from "../auth/AuthProvider";
import { renewalInfo, sumPremiums } from "./format";

/**
 * The Phase-2 data layer: ONE composed, client-side store of the caller's
 * scoped book, shared by Book of Business, the client hub, Today, and the
 * ⌘K client index.
 *
 * Source of truth is the deployed authz contract: every list endpoint
 * already returns an agent-scoped bare array (admins get everything), so
 * "the book" is simply what these lists return for the current token.
 * ~520 clients / ~1,000 policies compose fine in memory; the real
 * search/rollup endpoint stays flagged with the Coordinator as the eventual
 * server-side replacement.
 *
 * CRITICAL data rule (book migration 2026-07-10): merged records carry
 * EMPTY inverse arrays — client.autos/properties/umbrellas/documents are
 * [] by design. Households are therefore composed BY QUERY: policies are
 * grouped onto clients by matching policy.clientRef / policy.clientID
 * (string Client _id) against client._id — never by walking client.autos.
 */

const BookDataContext = createContext(null);

const LISTS = [
  { key: "clients", request: () => API.getClients() },
  { key: "autos", request: () => API.getAutos() },
  { key: "properties", request: () => API.getProperties() },
  { key: "umbrellas", request: () => API.getUmbrellas() },
  { key: "quotes", request: () => API.getQuotes() },
  { key: "requests", request: () => API.getServicerequests() },
  { key: "prospects", request: () => API.getProspects() }
];

function keysForClientMatch(record) {
  // A policy/request can point at its client via the normalized ObjectId ref
  // or the legacy string field; either may be absent. Collect all non-empty.
  return [record.clientRef, record.clientID, record.client]
    .filter(Boolean)
    .map((v) => String(v._id || v)); // tolerate populated refs
}

export function composeBook({
  clients,
  autos,
  properties,
  umbrellas,
  quotes,
  requests,
  prospects
}) {
  const policies = [
    ...autos.map((p) => ({ ...p, lob: "Auto", lobKey: "auto" })),
    ...properties.map((p) => ({ ...p, lob: "Home", lobKey: "home" })),
    ...umbrellas.map((p) => ({ ...p, lob: "Umbrella", lobKey: "umbrella" }))
  ];

  const policiesByClient = new Map();
  for (const policy of policies) {
    for (const key of keysForClientMatch(policy)) {
      if (!policiesByClient.has(key)) {
        policiesByClient.set(key, []);
      }
      // The same policy can carry both clientRef and clientID for one client;
      // dedupe per bucket by _id.
      const bucket = policiesByClient.get(key);
      if (!bucket.some((p) => p._id === policy._id)) {
        bucket.push(policy);
      }
    }
  }

  const quotesByClient = new Map();
  for (const quote of quotes) {
    // Weak linkage, flagged to the Coordinator: quotes only reference a
    // client via convertedToClientID once converted. Pre-conversion quotes
    // belong to the Pipeline (Phase 2.5), not a client hub.
    if (quote.convertedToClientID) {
      const key = String(quote.convertedToClientID);
      if (!quotesByClient.has(key)) {
        quotesByClient.set(key, []);
      }
      quotesByClient.get(key).push(quote);
    }
  }

  const requestsByClient = new Map();
  for (const request of requests) {
    for (const key of keysForClientMatch(request)) {
      if (!requestsByClient.has(key)) {
        requestsByClient.set(key, []);
      }
      requestsByClient.get(key).push(request);
    }
  }

  const households = clients.map((client) => {
    const id = String(client._id);
    const clientPolicies = policiesByClient.get(id) || [];
    const active = clientPolicies.filter((p) => p.policyActive !== false);
    const premiums = sumPremiums(active);
    const renewals = active
      .map((p) => ({ policy: p, renewal: renewalInfo(p) }))
      .filter((r) => r.renewal && r.renewal.date.getTime() >= Date.now())
      .sort((a, b) => a.renewal.date - b.renewal.date);
    return {
      id,
      client,
      name:
        client.accountName ||
        [client.firstName, client.lastName].filter(Boolean).join(" ") ||
        "Unnamed household",
      memberCount: Array.isArray(client.household) ? client.household.length : 0,
      policies: clientPolicies,
      activePolicies: active,
      // Guarded sum (P1): implausible premiums are excluded from the
      // household total and flagged, not silently summed or hidden.
      totalPremium: premiums.total,
      implausiblePremiums: premiums.excluded.length,
      nextRenewal: renewals.length > 0 ? renewals[0].renewal : null,
      quotes: quotesByClient.get(id) || [],
      requests: (requestsByClient.get(id) || []).sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )
    };
  });

  return { policies, households };
}

export function BookDataProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, failed: [], lists: null });

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.allSettled(LISTS.map((l) => l.request())).then((results) => {
      const lists = {};
      const failed = [];
      results.forEach((result, i) => {
        const { key } = LISTS[i];
        if (result.status === "fulfilled" && Array.isArray(result.value.data)) {
          lists[key] = result.value.data;
        } else {
          lists[key] = [];
          failed.push(key);
        }
      });
      setState({
        loading: false,
        // Clients are the spine — without them there is no book to show.
        // Other lists failing (e.g. a 403 for a future tier) degrade to
        // partial data, surfaced via `failed`.
        error: failed.includes("clients") ? "We couldn't load your book of business." : null,
        failed,
        lists
      });
    });
  }, []);

  useEffect(() => {
    if (user) {
      load();
    }
  }, [user, load]);

  const value = useMemo(() => {
    const composed = state.lists ? composeBook(state.lists) : { policies: [], households: [] };
    const householdById = new Map(composed.households.map((h) => [h.id, h]));
    return {
      loading: state.loading,
      error: state.error,
      failed: state.failed,
      households: composed.households,
      policies: composed.policies,
      quotes: state.lists ? state.lists.quotes : [],
      requests: state.lists ? state.lists.requests : [],
      prospects: state.lists ? state.lists.prospects : [],
      getHousehold: (id) => householdById.get(String(id)) || null,
      refresh: load
    };
  }, [state, load]);

  return <BookDataContext.Provider value={value}>{children}</BookDataContext.Provider>;
}

export function useBookData() {
  const ctx = useContext(BookDataContext);
  if (!ctx) {
    throw new Error("useBookData must be used inside <BookDataProvider>");
  }
  return ctx;
}

/**
 * Agent-name + split helpers. Agent attribution fields hold the Agent _id in
 * ~99% of records but a Firebase uid in ≥1 legacy record (the backend's
 * `_id | uid` invariant #3) — match on either.
 */
export function agentMatches(agent, value) {
  if (!agent || !value) {
    return false;
  }
  const v = String(value);
  return String(agent._id) === v || (agent.uid && String(agent.uid) === v);
}

export function resolveAgentName(agents, value) {
  const agent = (agents || []).find((a) => agentMatches(a, value));
  if (!agent) {
    return null;
  }
  return [agent.firstName, agent.lastName].filter(Boolean).join(" ") || agent.email || null;
}

/** The caller's own split % on a policy (D2), or null when unattributed. */
export function ownSplitFor(policy, identity) {
  if (!identity) {
    return null;
  }
  const slots = [
    ["mainAgent", "mainAgentSplit"],
    ["refAgentOne", "refAgentOneSplit"],
    ["refAgentTwo", "refAgentTwoSplit"],
    ["refAgentThree", "refAgentThreeSplit"],
    ["refAgentFour", "refAgentFourSplit"]
  ];
  for (const [agentField, splitField] of slots) {
    if (agentMatches(identity, policy[agentField])) {
      return policy[splitField] == null ? null : Number(policy[splitField]);
    }
  }
  return null;
}

/** All split participants on a policy (writing/admin view). */
export function allSplitsFor(policy, agents) {
  const slots = [
    ["mainAgent", "mainAgentSplit", "Main"],
    ["refAgentOne", "refAgentOneSplit", "Referral 1"],
    ["refAgentTwo", "refAgentTwoSplit", "Referral 2"],
    ["refAgentThree", "refAgentThreeSplit", "Referral 3"],
    ["refAgentFour", "refAgentFourSplit", "Referral 4"]
  ];
  return slots
    .filter(([agentField]) => policy[agentField] && policy[agentField] !== "notTagged")
    .map(([agentField, splitField, label]) => ({
      label,
      name: resolveAgentName(agents, policy[agentField]) || String(policy[agentField]),
      split: policy[splitField] == null ? null : Number(policy[splitField])
    }));
}
